import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FileStates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import SchemaTagTableSchemaSelector from 'metadata/SchemaTagTableSchemaSelector';
import TagTable from 'metadata/TagTable';
import {calcTagTableErrorsByFieldID, useTagTableState} from 'metadata/useTagTableState';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as ComputedTagUtil from 'schemas/tags/computed/ComputedTagUtil';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {getTagsToSet, getTagTableFriendlyTags, TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';
import StudyAssociationInput, {
  getStudyAssociations,
  StudyAssociationInputProps,
  useStudyAssociationInputProps,
} from 'studies/StudyAssociationInput';
import {StudySearchItem} from 'studies/StudySearchInput';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import {TemplateCollectionGraphQL} from '../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import {SchemaTag, Tag} from './FileConstants';

const clampName = (name: string, maxLength: number): string => name.slice(0, maxLength);

const isTemplateOrSubtemplate = (modelType: string): boolean =>
  modelType === ModelTypeConstants.SUBTEMPLATE || modelType === ModelTypeConstants.TEMPLATE;

interface BaseCreateModalContents {
  name: string;
  setName: (newName: string) => void;
  maxNameLength?: number;
  modelType: string;
  templateCollection?: TemplateCollectionGraphQL;
  setTemplateCollection: (collection: TemplateCollectionGraphQL) => void;
  showInventory?: boolean;
  showNotebook?: boolean;
  folderId?: string | null;
  setFolderId: (folderId: string) => void;
  tagSchema: TagSchema | null;
  updateTagSchema: (newTagSchema: TagSchema | null, initialTags?: Array<SchemaTag>) => void;
  updateSchemaTag: (tagIndex: number, newValue: Tag) => void;
  schemaTags: Array<SchemaTag>;
  entryProvenance?: 'entry' | 'template';
  errorsByFieldId: {[fieldId: number]: Array<SchemaValidationErrorRecord>};
  selectNameFieldInitially?: boolean;
  nameFieldLabel?: string;
  folderFieldLabel?: string;
  studyAssociationInputProps?: StudyAssociationInputProps | null;
}

/**
 * Stateless contents without the modal wrapper. Parent is responsible for all state management and actual
 * creation logic.
 */
export function BaseCreateModalContents({
  name,
  setName,
  maxNameLength = 255,
  modelType,
  templateCollection,
  setTemplateCollection,
  showInventory = false,
  showNotebook = false,
  folderId,
  setFolderId,
  tagSchema,
  updateTagSchema,
  updateSchemaTag,
  schemaTags,
  entryProvenance,
  errorsByFieldId,
  selectNameFieldInitially = true,
  nameFieldLabel = 'Name',
  folderFieldLabel,
  studyAssociationInputProps,
}: BaseCreateModalContents): JSX.Element {
  const ref = useRef(null);
  useEffect((): void => {
    if (selectNameFieldInitially) {
      (ReactDOM.findDOMNode(ref.current) as HTMLInputElement).select();
    }
  }, [selectNameFieldInitially]);

  function renderFolderOrTemplateCollection(): JSX.Element {
    if (isTemplateOrSubtemplate(modelType)) {
      return (
        <Col sm={5}>
          <FieldLabel label="Template collection" isRequired={true} />
          <TemplateCollectionSelector value={templateCollection} onChange={setTemplateCollection} />
        </Col>
      );
    }
    const selectProjectsOnly = Boolean(showInventory === false && showNotebook === false);
    const folderLabel =
      folderFieldLabel ?? (selectProjectsOnly ? 'Project' : DEFAULT_PROJECT_FOLDER_LABEL_TEXT);
    return (
      <Col sm={5}>
        <FormGroup>
          <FieldLabel label={folderLabel} isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showInventory={showInventory}
            showNotebook={showNotebook}
            value={folderId}
            onChange={(e) => {
              setFolderId(e.target.value);
            }}
            unsetOnFetchFail={true}
          />
        </FormGroup>
      </Col>
    );
  }
  return (
    <>
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'BaseCreateModal', element: 'name'}}>
            <FieldLabel label={nameFieldLabel} isRequired={true} />
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(clampName(e.target.value, maxNameLength));
              }}
              ref={ref}
            />
          </FormGroup>
        </Col>
        {renderFolderOrTemplateCollection()}
      </Row>
      {BenchlingConfig.STUDIES_ENABLE &&
        studyAssociationInputProps &&
        !isTemplateOrSubtemplate(modelType) && (
          <Row>
            <Col sm={12}>
              <FormGroup>
                <FieldLabel label="Studies" />
                <StudyAssociationInput {...studyAssociationInputProps} />
              </FormGroup>
            </Col>
          </Row>
        )}
      {BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
        (modelType === ModelTypeConstants.ENTRY || modelType === ModelTypeConstants.TEMPLATE) && (
          <Row>
            <Col sm={12}>
              <FieldLabel
                isRequired={BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW}
                label="Entry schema"
              />
              <TagTable
                canRefreshTags={
                  ComputedTagUtil.hasComputedTags(getTagTableFriendlyTags(schemaTags)) ||
                  IntegrationTagUtil.hasIntegrationTags(getTagTableFriendlyTags(schemaTags))
                }
                emptyMessage="This schema has no fields."
                errorsByFieldId={modelType === ModelTypeConstants.TEMPLATE ? {} : errorsByFieldId}
                tags={schemaTags}
                areTagsEditable={false}
                isLoadingTags={false}
                sequenceParts={null}
                subheaderNode={
                  <SchemaTagTableSchemaSelector
                    fileType={ModelTypeConstants.ENTRY}
                    isSchemaEditable={
                      modelType === ModelTypeConstants.TEMPLATE ||
                      entryProvenance === ModelTypeConstants.ENTRY
                    }
                    isFileRegistered={false}
                    onUpdateTagSchema={updateTagSchema}
                    tagSchema={tagSchema}
                  />
                }
                title={<div>Schema</div>}
                onUpdateTag={updateSchemaTag}
                isEditing={true}
              />
            </Col>
          </Row>
        )}
    </>
  );
}

interface BaseCreateModalProps {
  create: (
    name: string,
    folderOrTemplateCollectionId: string,
    tagSchemaId?: string,
    schemaTags?: TagUpdateObject,
    studyAssociations?: Array<StudyAssociation>,
    initialNoteParts?: Array<NotePart>
  ) => JQueryPromise<string>;
  initialFolderId?: string;
  initialStudySearchItems?: Array<StudySearchItem>;
  title: string;
  onRequestClose: () => void;
  initialName?: string;
  showInventory?: boolean;
  showNotebook?: boolean;
  maxNameLength?: number;
  dataTestAttrs?: DataTestAttrs;
  modelType?: string;
  initialTemplateCollection?: TemplateCollectionGraphQL;
  folderDefaultsKey: FolderDefaultsKey;
  initialTagSchema?: TagSchema | null;
  initialSchemaTags?: Array<SchemaTag>;
  // Describes if an entry was created from a template or not
  entryProvenance?: 'entry' | 'template';
  displayChangeStudyableAssignmentPermissionWarning?: boolean;
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
  initialNoteParts?: Array<NotePart>;
  hideStudiesInput?: boolean;
}

const BaseCreateModal = ({
  create,
  initialFolderId,
  initialStudySearchItems,
  title,
  onRequestClose,
  initialName,
  showInventory = false,
  showNotebook = false,
  maxNameLength = 255,
  dataTestAttrs,
  modelType = ModelTypeConstants.ENTRY,
  initialTemplateCollection,
  folderDefaultsKey = FolderDefaultsKey.INVENTORY,
  initialTagSchema,
  initialSchemaTags,
  entryProvenance = ModelTypeConstants.ENTRY,
  displayChangeStudyableAssignmentPermissionWarning,
  autoLinkingStudy,
  initialNoteParts,
  hideStudiesInput = false,
}: BaseCreateModalProps): JSX.Element => {
  const [name, setName] = useState(initialName != null ? clampName(initialName, maxNameLength) : 'Untitled');
  const [folderId, setFolderId] = useState(
    initialFolderId != null ? initialFolderId : FolderDefaults.get(folderDefaultsKey)
  );
  const [posting, setPosting] = useState<boolean>(false);
  const [templateCollection, setTemplateCollection] = useState<TemplateCollectionGraphQL | undefined>(
    initialTemplateCollection
  );
  const {
    schemaTags: fieldValues,
    tagSchema,
    updateSchemaTag,
    updateTagSchema,
  } = useTagTableState(initialTagSchema, initialSchemaTags);

  const errorsByFieldId = tagSchema ? calcTagTableErrorsByFieldID(tagSchema, fieldValues) : {};
  const studyAssociationInputProps: StudyAssociationInputProps = useStudyAssociationInputProps({
    initialStudySearchItems: initialStudySearchItems ?? [],
    displayPermissionWarning: displayChangeStudyableAssignmentPermissionWarning,
    autoLinkingStudy,
  });
  function onRequestSubmit(): void {
    setPosting(true);
    const tagUpdates = BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL
      ? getTagsToSet(fieldValues)
      : {};

    const studyAssociations = getStudyAssociations(studyAssociationInputProps);

    if (isTemplateOrSubtemplate(modelType)) {
      create(name, templateCollection!.id, tagSchema?.id, tagUpdates, studyAssociations)
        .fail(notifications.notifyError)
        .done((templateId: string) => {
          FileStates.get().openFolderItem(templateId);
          onRequestClose();
        })
        .always(() => {
          setPosting(false);
        });
    } else if (modelType === ModelTypeConstants.ENTRY) {
      // Submit is disabled unless folder is selected on modal
      FolderDefaults.set(folderDefaultsKey, folderId!);
      create(name, folderId!, tagSchema?.id, tagUpdates, studyAssociations, initialNoteParts)
        .fail(notifications.notifyError)
        .done((fileId: string) => {
          FolderDAOCache.invalidateFolderContents(folderId);
          FileStates.get().openFolderItem(fileId);
          onRequestClose();
        })
        .always(() => {
          setPosting(false);
        });
    } else {
      // Submit is disabled unless folder is selected on modal
      FolderDefaults.set(folderDefaultsKey, folderId!);
      create(name, folderId!)
        .fail(notifications.notifyError)
        .done((fileId: string) => {
          FolderDAOCache.invalidateFolderContents(folderId);
          FileStates.get().openFolderItem(fileId);
          onRequestClose();
        })
        .always(() => {
          setPosting(false);
        });
    }
  }

  function getDisabled(): boolean {
    let disabled;
    if (isTemplateOrSubtemplate(modelType)) {
      disabled = name.trim().length === 0 || templateCollection == null || posting;
    } else if (modelType === ModelTypeConstants.ENTRY) {
      disabled =
        name.trim().length === 0 ||
        folderId == null ||
        posting ||
        // Disable if there is no schema but schema is required for review
        (BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
        BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW
          ? tagSchema == null
          : false) ||
        // Disable if required fields are required on create but missing
        (BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL &&
          BenchlingConfig.ELN_ENFORCE_REQUIRED_FIELDS_ON_ENTRY_CREATE &&
          Object.keys(errorsByFieldId).length > 0);
    } else {
      disabled = name.trim().length === 0 || folderId == null || posting;
    }
    // Disable if the user is missing the change studyable assignment permission for a study folder
    disabled ||= (studyAssociationInputProps.invalidItemNames ?? []).length > 0;
    return disabled;
  }

  return (
    <Modal
      title={title}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: posting ? 'Creating...' : 'Create',
          submit: true,
          disabled: getDisabled(),
          dataTestAttrs,
        },
      ]}
    >
      <BaseCreateModalContents
        name={name}
        setName={setName}
        maxNameLength={maxNameLength}
        modelType={modelType}
        templateCollection={templateCollection}
        setTemplateCollection={setTemplateCollection}
        showInventory={showInventory}
        showNotebook={showNotebook}
        folderId={folderId}
        setFolderId={setFolderId}
        tagSchema={tagSchema}
        updateSchemaTag={updateSchemaTag}
        updateTagSchema={updateTagSchema}
        schemaTags={fieldValues}
        entryProvenance={entryProvenance}
        errorsByFieldId={errorsByFieldId}
        studyAssociationInputProps={hideStudiesInput ? undefined : studyAssociationInputProps}
      />
    </Modal>
  );
};

export default BaseCreateModal;
